// @flow
import styled from '@emotion/styled'
import { useLocation } from '@reach/router'
import { Link, navigate } from 'gatsby'
import transparentize from 'polished/lib/color/transparentize'
import * as React from 'react'

import { colors, media } from '@toggl/style'

import { Select } from '../form/Select'
import * as Responsive from '../responsive'
import { defaultText } from '../text'

type Props = {
  tabs: $ReadOnlyArray<{
    id: string,
    title: string,
  }>,
}

export function useActiveTab(tabs: $PropertyType<Props, 'tabs'>): string {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const tab = params.get('tab') || tabs[0].id
  const activeTab = tabs.find(({ id }) => id === tab) ? tab : tabs[0].id
  return activeTab
}

export function TabsList({ tabs }: Props) {
  const activeTab = useActiveTab(tabs)

  return (
    <React.Fragment>
      <Responsive.MobileOnly>
        <Select
          id="tabs"
          name="tabs"
          onChange={onTabChange}
          label=""
          value={activeTab}
        >
          {tabs.map(({ id, title }) => (
            <option key={id} value={id}>
              {title}
            </option>
          ))}
        </Select>
      </Responsive.MobileOnly>
      <Responsive.DesktopOnly>
        <WrapperRoot>
          {tabs.map(({ id, title }) => (
            <Tab active={id === activeTab} id={id} key={id} title={title} />
          ))}
        </WrapperRoot>
      </Responsive.DesktopOnly>
    </React.Fragment>
  )
}

const WrapperRoot: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 80px;

  background-color: ${colors.almostWhite};
`

type TabProps = {|
  +active: boolean,
  +id: string,
  +title: string,
|}

export function Tab({ active, id, title }: TabProps) {
  return (
    <TabLink to={`?tab=${id}#view`} aria-label={title} active={active}>
      <Title active={active}>{title}</Title>
    </TabLink>
  )
}

const TabLink: React.ComponentType<any> = styled(Link)`
  flex: 1 auto;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ active }) =>
    active ? colors.fadedPurple : colors.almostWhite};
  cursor: ${({ active }) => (active ? 'default' : 'pointer')};

  &:not(:last-child) {
    border-right: 1px solid ${transparentize(0.9, colors.fadedPurpleAlt)};
  }

  &:hover {
    div {
      ${({ active }) =>
        active ? '' : `border-bottom: 1px solid ${colors.fadedPurple}`}
    }
  }
`

const Title: React.ComponentType<any> = styled.div`
  ${defaultText};
  color: ${({ active }) => (active ? colors.almostWhite : colors.darkPurple)};
  padding-bottom: 2px;
  display: none;
  ${media.mq[1]} {
    display: inherit;
  }
`

const onTabSelected = (id: string) => {
  navigate(`?tab=${id}#view`)
}

const onTabChange = (evt: SyntheticInputEvent<HTMLSelectElement>) =>
  onTabSelected(evt.currentTarget.value)
